import { Bot, Calendar, ChevronDown, ChevronUp, Clock, ExternalLink, Filter, Newspaper, Zap } from "lucide-react";
import { useState } from "react";

import type { MarketNews } from "../../core";

export default function TickerNews({ news, loading }: { news: MarketNews[] | null; loading: boolean }) {
	const [sentimentFilter, setSentimentFilter] = useState<string>("all");
	const [importantOnly, setImportantOnly] = useState<boolean>(true);
	const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());

	const toggleExpand = (id: string | number) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const getSentimentColor = (sentiment: string) => {
		if (sentiment === "bullish") return "text-emerald-700 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-400/10";
		if (sentiment === "bearish") return "text-rose-700 bg-rose-100 dark:text-rose-400 dark:bg-rose-400/10";
		return "text-zinc-600 bg-zinc-200 dark:text-zinc-400 dark:bg-zinc-800"; // Neutral
	};

	const formatDateTime = (dateString?: string) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return "N/A";
		return date.toLocaleString(undefined, {
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	if (loading) return <div className="h-64 rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 animate-pulse mt-6" />;

	const hasNews = Boolean(news && news.length > 0);

	// Filter the news array by sentiment AND relevance score
	const filteredNews = hasNews
		? news!.filter((item: MarketNews) => {
				const matchesSentiment = sentimentFilter === "all" ? true : item.sentiment === sentimentFilter;
				const matchesImportance = importantOnly ? (item.relevance_score ?? 0) > 0.75 : true;
				return matchesSentiment && matchesImportance;
			})
		: [];

	const sortedNews = filteredNews.sort((a, b) => {
		const dateA = new Date(a.published_at || a.created_at || 0).getTime();
		const dateB = new Date(b.published_at || b.created_at || 0).getTime();
		return dateB - dateA; // Sort descending (newest first)
	});

	return (
		<div className="rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-6 shadow-md">
			<div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
				<h3 className="text-xl font-semibold">Relevant Market News</h3>

				{/* Filters Row */}
				{hasNews && (
					<div className="flex flex-wrap items-center gap-2">
						{/* Important Filter Toggle */}
						<button
							onClick={() => setImportantOnly(!importantOnly)}
							className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors shrink-0 ${
								importantOnly
									? "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-400/10 dark:text-amber-400 dark:border-amber-400/30"
									: "bg-zinc-100 text-zinc-600 border-zinc-300 hover:text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-950 dark:text-zinc-400 dark:border-zinc-800 dark:hover:text-zinc-200 dark:hover:bg-zinc-900"
							}`}
						>
							<Zap
								size={12}
								className={
									importantOnly
										? "fill-amber-600 text-amber-600 dark:fill-amber-400 dark:text-amber-400"
										: "text-zinc-400 dark:text-zinc-500"
								}
							/>
							Important Only (&gt;0.75)
						</button>

						{/* Sentiment Filter Toggle */}
						<div className="flex items-center gap-1 p-1 bg-zinc-100 border border-zinc-300 dark:bg-zinc-950 dark:border-zinc-800 rounded-lg overflow-x-auto">
							<Filter size={14} className="text-zinc-400 dark:text-zinc-500 mx-2 shrink-0" />
							{["all", "bullish", "bearish", "neutral"].map((filterName) => {
								let activeStyle = "bg-zinc-300 text-zinc-900 dark:bg-zinc-800 dark:text-white";
								if (filterName === "bullish")
									activeStyle = "bg-emerald-200 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-400";
								if (filterName === "bearish") activeStyle = "bg-rose-200 text-rose-800 dark:bg-rose-400/20 dark:text-rose-400";
								if (filterName === "neutral") activeStyle = "bg-zinc-300 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-300";

								const isActive = sentimentFilter === filterName;

								return (
									<button
										key={filterName}
										onClick={() => setSentimentFilter(filterName)}
										className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors shrink-0 ${
											isActive
												? activeStyle
												: "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-200 dark:text-zinc-500 dark:hover:text-zinc-300 dark:hover:bg-zinc-900"
										}`}
									>
										{filterName}
									</button>
								);
							})}
						</div>
					</div>
				)}
			</div>

			{!hasNews ? (
				<div className="flex flex-col items-center justify-center py-10 px-4 text-center border border-dashed border-zinc-300 rounded-lg bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/50 mt-2">
					<Newspaper className="text-zinc-400 dark:text-zinc-600 mb-3" size={32} />
					<p className="text-zinc-700 dark:text-zinc-300 font-medium">No market news available</p>
					<p className="text-xs text-zinc-500 mt-1 max-w-sm">
						Articles relevant to your portfolio positions will automatically appear here once fetched.
					</p>
				</div>
			) : (
				/* List Container with sleek dividers */
				<div className="flex flex-col divide-y divide-zinc-200/50 dark:divide-zinc-800/50">
					{sortedNews.length === 0 ? (
						<div className="text-center py-8 text-zinc-500 border border-dashed border-zinc-300 dark:border-zinc-800 rounded-lg mt-2">
							No news matching active filters{importantOnly ? " (relevance > 0.75)" : ""}.
						</div>
					) : (
						sortedNews.map((item: any) => {
							const isHighRelevance = (item.relevance_score ?? 0) > 0.75;
							const relevancePercent = Math.round((item.relevance_score ?? 0) * 100);
							const isExpanded = expandedIds.has(item.id);

							// Metadata field fallbacks
							const modelName = item.model || item.summarized_by || item.model_name || "N/A";
							const summarizedAt = item.summarized_at || item.created_at;
							const sourceName = item.source || item.source_name || "Source";

							return (
								<div
									key={item.id}
									className="group flex flex-col py-3 px-2 -mx-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800/40 transition-colors"
								>
									{/* Main Row / Accordion Header */}
									<div
										onClick={() => toggleExpand(item.id)}
										className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 cursor-pointer select-none"
									>
										{/* Ticker & Tag Column */}
										<div className="flex sm:flex-col items-center sm:items-start gap-2 sm:gap-1.5 shrink-0 w-24">
											<span className="font-bold text-blue-600 dark:text-blue-400 text-sm">{item.ticker}</span>
											<span
												className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${getSentimentColor(
													item.sentiment,
												)}`}
											>
												{item.sentiment}
											</span>
										</div>

										{/* Article Content Column */}
										<div className="flex-1 min-w-0">
											<h4 className="text-sm text-zinc-800 dark:text-zinc-200 font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
												{item.title}
											</h4>
											{!isExpanded && <p className="text-xs text-zinc-500 truncate mt-0.5">{item.summary}</p>}
										</div>

										{/* Relevance Badge, Date, Easy-to-Click Source Button & Expand Toggle */}
										<div className="flex flex-wrap sm:flex-nowrap items-center justify-between sm:justify-end gap-2.5 shrink-0 text-zinc-500">
											{/* Relevance Score Badge */}
											<span
												className={`flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border ${
													isHighRelevance
														? "text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-400 dark:bg-amber-400/10 dark:border-amber-400/20"
														: "text-zinc-600 bg-zinc-100 border-zinc-200 dark:text-zinc-400 dark:bg-zinc-800/60 dark:border-zinc-700/50"
												}`}
												title={`Relevance score: ${item.relevance_score}`}
											>
												{isHighRelevance && <Zap size={10} className="fill-amber-600 dark:fill-amber-400" />}
												{relevancePercent}% rel
											</span>

											<span className="text-xs whitespace-nowrap text-zinc-500 dark:text-zinc-400">
												{new Date(item.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
											</span>

											{/* HIGH-AFFORDANCE LINK BUTTON */}
											<a
												href={item.url}
												target="_blank"
												rel="noreferrer"
												onClick={(e) => e.stopPropagation()}
												className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-300 hover:border-blue-400 dark:text-blue-400 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 dark:border-blue-500/30 dark:hover:border-blue-500/50 rounded-lg transition-all active:scale-95 shrink-0"
												title="Open original article in new tab"
											>
												<span>{sourceName}</span>
												<ExternalLink size={13} className="shrink-0" />
											</a>

											<button
												type="button"
												className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-200 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 rounded-md transition-colors shrink-0"
												aria-label={isExpanded ? "Collapse details" : "Expand details"}
											>
												{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
											</button>
										</div>
									</div>

									{/* Expanded Details Panel */}
									{isExpanded && (
										<div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-800/80 text-xs text-zinc-700 dark:text-zinc-300 space-y-3 sm:pl-28">
											{/* Full Summary */}
											<div>
												<div className="text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
													Full Summary
												</div>
												<p className="leading-relaxed text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950/60 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800/60 whitespace-pre-line">
													{item.full_summary || item.summary || "No full summary available."}
												</p>
											</div>

											{/* Metadata Cards */}
											<div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-zinc-500 dark:text-zinc-400 pt-1">
												<div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-950/40 p-2 rounded-lg border border-zinc-200 dark:border-zinc-800/40">
													<Bot size={14} className="text-blue-600 dark:text-blue-400 shrink-0" />
													<div className="min-w-0">
														<div className="text-[10px] text-zinc-500">Model Used</div>
														<div className="font-mono text-zinc-700 dark:text-zinc-300 truncate text-[11px]">
															{modelName}
														</div>
													</div>
												</div>

												<div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-950/40 p-2 rounded-lg border border-zinc-200 dark:border-zinc-800/40">
													<Calendar size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
													<div className="min-w-0">
														<div className="text-[10px] text-zinc-500">Published At</div>
														<div className="text-zinc-700 dark:text-zinc-300 text-[11px] truncate">
															{formatDateTime(item.published_at)}
														</div>
													</div>
												</div>

												<div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-950/40 p-2 rounded-lg border border-zinc-200 dark:border-zinc-800/40">
													<Clock size={14} className="text-amber-600 dark:text-amber-400 shrink-0" />
													<div className="min-w-0">
														<div className="text-[10px] text-zinc-500">Summarized At</div>
														<div className="text-zinc-700 dark:text-zinc-300 text-[11px] truncate">
															{formatDateTime(summarizedAt)}
														</div>
													</div>
												</div>
											</div>

											{/* Expanded Action Button - PERFECT MIDDLE GROUND */}
											<div className="pt-2 flex items-center justify-end">
												<a
													href={item.url}
													target="_blank"
													rel="noreferrer"
													onClick={(e) => e.stopPropagation()}
													className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-300 hover:border-blue-400 hover:text-blue-800 dark:text-blue-300 dark:bg-blue-950/50 dark:hover:bg-blue-900/50 dark:border-blue-800/60 dark:hover:border-blue-700 dark:hover:text-blue-200 rounded-lg transition-all shadow-sm w-full sm:w-auto"
												>
													<span>Read full article on {sourceName}</span>
													<ExternalLink size={14} className="text-blue-600 dark:text-blue-400" />
												</a>
											</div>
										</div>
									)}
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
}
